// React imports
import { useMemo, useEffect, useState } from 'react';

// Third-party imports
import { useSelector, useDispatch } from 'react-redux';

// Types
import AddOnService from '@spike/addon-service-model';
import { RootState } from 'store';
import Service, { SERVICES_BUSINESS_AREAS } from 'model/Service';
import { EnhancedProduct } from './useDiscountForm';

// Actions
import { fetchThunk } from '@spike/addon-services-action';
import { fetchProductsThunk } from '@spike/products-action';
import { fetchServicesThunk } from 'actions/services/ServicesActions';

// Hooks
import { useApiClientWrapper } from 'hooks';
import useServiceCategories from 'hooks/useServiceCategories';

interface UseDiscountServicesReturn {
    groomingServices: Service[];
    daycareServices: Service[];
    boardingServices: Service[];
    transportationServices: Service[];
    evaluationServices: Service[];
    addons: AddOnService[];
    products: EnhancedProduct[];
    isLoadingData: boolean;
    fetchAllServices: () => void;
}

/**
 * useDiscountServices - Redux Integration and Data Fetching Hook
 *
 * Purpose:
 * Manages the integration with Redux store to fetch and provide all service-related data
 * needed for the discount form (services, add-ons, and products).
 *
 * Responsibilities:
 * - Connects to Redux store selectors for all service categories (grooming, daycare, boarding, transportation, evaluation)
 * - Manages fetching of services, add-ons, and products from the backend
 * - Provides centralized loading state management across all data sources
 * - Optimizes fetching logic (only re-fetches grooming/dental, caches other categories)
 * - Transforms product data to include enhanced fields needed by the discount system
 *
 * Key Features:
 * - Smart fetching: Grooming and dental services always refresh, others use cache if available
 * - Unified loading state: Tracks loading across all data sources and provides single boolean flag
 * - Service category awareness: Only fetches enabled service categories based on business configuration
 * - Product enhancement: Adds productId field to business products for discount item mapping
 * - First fetch tracking: Prevents showing loading state after initial data load
 *
 * When to use:
 * Use this hook when you need access to all available services, add-ons, and products
 * for discount configuration. This hook handles all Redux interactions and data fetching.
 *
 * @returns {UseDiscountServicesReturn} Object containing all services, loading state, and fetch function
 */
export const useDiscountServices = (): UseDiscountServicesReturn => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const [hasFetchedOnce, setHasFetchedOnce] = useState<boolean>(false);

    // Service data selectors: filtered to get only active services
    const groomingServices = useSelector((state: RootState) =>
        state.services.servicesByBusinessArea.grooming.services.filter(service => service.status.id === 1)
    );
    // const mobileGrooming = useSelector((state: RootState) => state.services.servicesByBusinessArea.mobileGrooming.services);
    const daycareServices = useSelector((state: RootState) =>
        state.services.servicesByBusinessArea.daycare.services.filter(service => service.status.id === 1)
    );
    const boardingServices = useSelector((state: RootState) =>
        state.services.servicesByBusinessArea.boarding.services.filter(service => service.status.id === 1)
    );
    const transportationServices = useSelector((state: RootState) =>
        state.services.servicesByBusinessArea.transportation.services.filter(service => service.status.id === 1)
    );
    const evaluationServices = useSelector((state: RootState) =>
        state.services.servicesByBusinessArea.evaluation.services.filter(service => service.status.id === 1)
    );
    const addons = useSelector((state: RootState) =>
        state.addOnServices.services.filter(service => service.active === true)
    );
    const productsFromRedux = useSelector((state: RootState) => state.products.list.page ?? []);

    // Service category flags
    const { hasGrooming, hasDaycare, hasBoarding, hasTransportation, hasEvaluation } = useServiceCategories();

    // Transform products to EnhancedProduct by copying businessProduct.id to businessProduct.productId
    const products: EnhancedProduct[] = useMemo(() => {
        return productsFromRedux.map(product => ({
            ...product,
            businessProduct: product.businessProduct
                ? {
                      ...product.businessProduct,
                      productId: product.businessProduct.id
                  }
                : undefined
        }));
    }, [productsFromRedux]);

    // Loading states for each data source
    const isLoadingGroomingServices = useSelector(
        (state: RootState) => state.services.servicesByBusinessArea.grooming.loading
    );
    const isLoadingDaycareServices = useSelector(
        (state: RootState) => state.services.servicesByBusinessArea.daycare.loading
    );
    const isLoadingBoardingServices = useSelector(
        (state: RootState) => state.services.servicesByBusinessArea.boarding.loading
    );
    const isLoadingTransportationServices = useSelector(
        (state: RootState) => state.services.servicesByBusinessArea.transportation.loading
    );
    const isLoadingEvaluationServices = useSelector(
        (state: RootState) => state.services.servicesByBusinessArea.evaluation.loading
    );
    const isLoadingAddons = useSelector((state: RootState) => state.addOnServices.loading);
    const isLoadingProducts = useSelector((state: RootState) => state.products.loading);

    // Compute if any service is currently loading
    const isLoadingData = useMemo(() => {
        const anyLoading =
            isLoadingGroomingServices ||
            isLoadingDaycareServices ||
            isLoadingBoardingServices ||
            isLoadingTransportationServices ||
            isLoadingEvaluationServices ||
            isLoadingAddons ||
            isLoadingProducts;

        // Check if we have all the necessary data
        const hasAllData =
            groomingServices.length > 0 ||
            daycareServices.length > 0 ||
            boardingServices.length > 0 ||
            transportationServices.length > 0 ||
            evaluationServices.length > 0 ||
            addons.length > 0 ||
            productsFromRedux.length > 0;

        // If we have data or completed first fetch, only show loading if actively fetching
        if (hasFetchedOnce || hasAllData) {
            return anyLoading;
        }

        // If no data yet and haven't fetched, consider it loading
        return true;
    }, [
        hasFetchedOnce,
        isLoadingGroomingServices,
        isLoadingDaycareServices,
        isLoadingBoardingServices,
        isLoadingTransportationServices,
        isLoadingEvaluationServices,
        isLoadingAddons,
        isLoadingProducts,
        groomingServices.length,
        daycareServices.length,
        boardingServices.length,
        transportationServices.length,
        evaluationServices.length,
        addons.length,
        productsFromRedux.length
    ]);

    // Fetch all services function - grooming and dental always fetch (if enabled), others only if data doesn't exist
    const fetchAllServices = useMemo(
        () => () => {
            const businessAreas = [
                {
                    area: SERVICES_BUSINESS_AREAS.GROOMING + ',' + SERVICES_BUSINESS_AREAS.DENTAL,
                    hasData: groomingServices.length > 0,
                    alwaysFetch: true,
                    enabled: hasGrooming
                },
                {
                    area: SERVICES_BUSINESS_AREAS.DAYCARE,
                    hasData: daycareServices.length > 0,
                    alwaysFetch: false,
                    enabled: hasDaycare
                },
                {
                    area: SERVICES_BUSINESS_AREAS.BOARDING,
                    hasData: boardingServices.length > 0,
                    alwaysFetch: false,
                    enabled: hasBoarding
                },
                {
                    area: SERVICES_BUSINESS_AREAS.TRANSPORTATION,
                    hasData: transportationServices.length > 0,
                    alwaysFetch: false,
                    enabled: hasTransportation
                },
                {
                    area: SERVICES_BUSINESS_AREAS.EVALUATION,
                    hasData: evaluationServices.length > 0,
                    alwaysFetch: false,
                    enabled: hasEvaluation
                }
            ];

            // Fetch services: only if enabled AND (always fetch OR no data exists)
            businessAreas.forEach(({ area, hasData, alwaysFetch, enabled }) => {
                if (enabled && (alwaysFetch || !hasData)) {
                    dispatch(
                        fetchServicesThunk(undefined, undefined, undefined, undefined, undefined, undefined, area)
                    );
                }
            });

            // Fetch addons if not already loaded
            if (addons.length === 0) {
                dispatch(fetchThunk(apiClientWrapper));
            }

            // Fetch products if not already loaded
            if (productsFromRedux.length === 0) {
                dispatch(fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000));
            }
        },
        [
            dispatch,
            apiClientWrapper,
            groomingServices,
            daycareServices,
            boardingServices,
            transportationServices,
            evaluationServices,
            addons,
            productsFromRedux,
            hasGrooming,
            hasDaycare,
            hasBoarding,
            hasTransportation,
            hasEvaluation
        ]
    );

    // Track when all data fetches have finished loading for the first time
    useEffect(() => {
        const allFetchesComplete =
            !isLoadingGroomingServices &&
            !isLoadingDaycareServices &&
            !isLoadingBoardingServices &&
            !isLoadingTransportationServices &&
            !isLoadingEvaluationServices &&
            !isLoadingAddons &&
            !isLoadingProducts;

        if (allFetchesComplete && !hasFetchedOnce) {
            setHasFetchedOnce(true);
        }
    }, [
        hasFetchedOnce,
        isLoadingGroomingServices,
        isLoadingDaycareServices,
        isLoadingBoardingServices,
        isLoadingTransportationServices,
        isLoadingEvaluationServices,
        isLoadingAddons,
        isLoadingProducts
    ]);

    return useMemo(
        () => ({
            groomingServices,
            daycareServices,
            boardingServices,
            transportationServices,
            evaluationServices,
            addons,
            products,
            isLoadingData,
            fetchAllServices
        }),
        [
            groomingServices,
            daycareServices,
            boardingServices,
            transportationServices,
            evaluationServices,
            addons,
            products,
            isLoadingData,
            fetchAllServices
        ]
    );
};
